"use client";

import React, { useState, useEffect } from "react";
import NextLink from "next/link";
import {
  Box,
  Typography,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  Checkbox,
  Link,
} from "@mui/material";
import { useRouter } from "next/navigation";
import { APPLICATION_ID } from "@/constants";
import { useGetUserById } from "@/store/hooks/user";
import InputField from "@/component/SharedUI/Inputs/InputField";
import PasswordInputFiled from "@/component/SharedUI/Inputs/PasswordInputField";
import { PersonIcon, LockIcon, EmailIcon, AddressIcon } from "@/lib/icons";
import PrivacyPolicy from "../PrivacyPolicy";
import TermsAndServices from "../TermsAndServices";
import TermsAndServices2 from "../TermsAndServices2";
import { useSignUp } from "@/store/hooks/auth";
import { useSearchParams } from "next/navigation";
import { StateOptions, PhilippinesProvinceOptions } from "@/lib/statesData";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Dropdown from "@/component/SharedUI/Inputs/Dropdown";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

type Props = {
  layoutHandler: any;
  showAllHandler: any;
};

type UserObject = {
  fullName: string;
  phoneNumber: string;
  email: string;
  country: string;
  city: string;
  state: string;
  // address1: string;
  password: string;
  confirmPassword: string;
  acceptTermsOfServices: boolean;
  acceptPrivacyPolicy: boolean;
};

const UserDataForm = ({ showAllHandler, layoutHandler }: Props) => {
  const [email, setEmail] = useState("");

  const params = useSearchParams();

  const referrerDriverId = params.get("referrerDriverId") as string;

  const { data: userData, isError: userError } = useGetUserById("");

  // Set email when user data is loaded
  useEffect(() => {
    if (userData?.email) {
      setEmail(userData.email);
    }
  }, [userData]);

  // Handle error when userId is invalid
  useEffect(() => {
    if (userError) {
      console.error("Error fetching user data:", userError);
      // Clear the email field if there's an error
      setEmail("");
    }
  }, [userError]);

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // PhoneInput value already includes dial code digits (e.g. "19171234567" for US, "639171234567" for PH)
  const isValidPhone = (phoneValue: string, country: string): boolean => {
    const digits = phoneValue.replace(/\D/g, "");
    if (country === "PH") return digits.length === 12; // 63 + 10
    return digits.length === 11; // 1 + 10
  };

  const handlePhoneChange = (value: string, countryData: any) => {
    const newCountry =
      countryData?.countryCode?.toUpperCase() === "PH" ? "PH" : "US";
    setAuthData((prev) => ({
      ...prev,
      phoneNumber: value,
      country: newCountry,
      ...(newCountry !== prev.country && { state: "" }),
    }));
    setErrors((prev) => ({ ...prev, phoneNumber: "" }));
  };

  // New Code
  const passwordRegex = /^.{4,}$/;
  const cityRegex = /^[a-zA-Z]+(?:[ '\-][a-zA-Z]+)*$/;
  const stateRegex =
    /^(Alabama|AL|Alaska|AK|Arizona|AZ|Arkansas|AR|California|CA|Colorado|CO|Connecticut|CT|Delaware|DE|Florida|FL|Georgia|GA|Hawaii|HI|Idaho|ID|Illinois|IL|Indiana|IN|Iowa|IA|Kansas|KS|Kentucky|KY|Louisiana|LA|Maine|ME|Maryland|MD|Massachusetts|MA|Michigan|MI|Minnesota|MN|Mississippi|MS|Missouri|MO|Montana|MT|Nebraska|NE|Nevada|NV|New\sHampshire|NH|New\sJersey|NJ|New\sMexico|NM|New\sYork|NY|North\sCarolina|NC|North\sDakota|ND|Ohio|OH|Oklahoma|OK|Oregon|OR|Pennsylvania|PA|Rhode\sIsland|RI|South\sCarolina|SC|South\sDakota|SD|Tennessee|TN|Texas|TX|Utah|UT|Vermont|VT|Virginia|VA|Washington|WA|West\sVirginia|WV|Wisconsin|WI|Wyoming|WY)$/i;

  const passwordMessage = "Password must be at least 4 characters long";

  const [isPasswordMatched, setIsPasswordMatched] = useState("");

  const { trigger, isSuccess, isError, isMutating, reset } = useSignUp();

  const [authData, setAuthData] = useState<UserObject>({
    fullName: "",
    phoneNumber: "",
    email: "",
    // address1: "",
    country: "US",
    city: "",
    state: "",
    password: "",
    confirmPassword: "",
    // please un-comment this code if you want privacy & terms of services pages
    // acceptTermsOfServices: false,
    // acceptPrivacyPolicy: false,
    // please un-comment this code if you want privacy & terms of services pages
    acceptTermsOfServices: false,
    acceptPrivacyPolicy: true,
  });

  const [errors, setErrors] = useState({
    fullName: "",
    phoneNumber: "",
    email: email,
    city: "",
    state: "",
    // address1: "",
    password: "",
    confirmPassword: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const onChangeHandler = (event: any) => {
    const { value, name } = event.target;

    setAuthData((prevState) => ({
      ...prevState,
      [name]: value,
      // reset state when country changes
      ...(name === "country" && { state: "" }),
    }));

    // Clear the error message as soon as the user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const [steps, setSteps] = useState({
    userDataForm: true,
    termsAndServicesForm: false,
    // privacyPolicyForm: false,
  });

  const stepsHandler = (step: string) => {
    if (step === "userDataForm") {
      showAllHandler();
    }

    setSteps((prevState) => ({
      userDataForm: false,
      termsAndServicesForm: false,
      // privacyPolicyForm: false,
      [step]: true,
    }));
  };
  //

  const step1Handler = () => {
    // reset();

    // Error Handling Code
    const digitsOnly = authData.phoneNumber.replace(/\D/g, "");
    const newErrors = {
      fullName: authData.fullName ? "" : "Full Name is required.",

      phoneNumber: isValidPhone(authData.phoneNumber.trim(), authData.country)
        ? ""
        : digitsOnly.length === 0
          ? "Phone Number is required."
          : authData.country === "PH"
            ? `Please enter a valid Philippine number (e.g. 0917-123-4567). (${digitsOnly.length} digits entered)`
            : digitsOnly.length < 10
              ? `Phone Number must be 10 digits. (${digitsOnly.length}/10)`
              : "Please enter a valid 10-digit phone number.",

      email: isValidEmail(authData.email.trim())
        ? ""
        : "Valid Email Address is required.",

      city: cityRegex.test(authData.city.trim())
        ? ""
        : "Please enter valid city name.",

      state: authData.state.trim() ? "" : "Please select a state/province.",

      // address1: authData.address1
      //   ? ""
      //   : "Address Line 1 is required. This is where we will send your Rider Free gift",

      password:
        authData.password.trim() && authData?.password.trim().length >= 4
          ? ""
          : "Password must be at least 4 characters long.",

      confirmPassword:
        authData.confirmPassword.trim() &&
        authData?.confirmPassword.trim().length >= 4
          ? ""
          : "Confirm Password must be at least 4 characters long.",
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((err) => err !== "");
    if (hasErrors) return;

    if (authData.password.trim() !== authData.confirmPassword.trim()) {
      return;
    }

    layoutHandler("normal");

    stepsHandler("termsAndServicesForm");
  };

  // PhoneInput already provides value with dial code digits, just prepend +
  const formatToE164 = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    return `+${digits}`;
  };

  const registerUserHandler = () => {
    reset();

    setIsLoading(true);

    const nameParts = authData.fullName.trim().split(/\s+/);
    const firstName = nameParts[0];
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "-";

    const user = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phoneNumber: formatToE164(authData?.phoneNumber.trim()),
      email: authData.email.trim(),
      country: authData.country,
      city: authData.city.trim(),
      state: authData.state.trim(),
      password: authData.password.trim(),
      acceptTermsOfServices: authData.acceptTermsOfServices,
      acceptPrivacyPolicy: authData.acceptPrivacyPolicy,
      adminCanApproach: true,
      // address1: authData?.address1,
      userType: "driver",
      // only include referrerDriverId if the referrerDriverId is there
      ...(referrerDriverId && { referrerDriverId }),
    };

    setIsLoading(true);
    trigger(user); // Call API with correct payload
  };

  useEffect(() => {
    if (isSuccess) {
      // Save basic user info to localStorage
      localStorage.setItem(
        "userInfo",
        JSON.stringify({
          fullName: authData.fullName,
          phoneNumber: authData.phoneNumber,
          email: authData.email,
          country: authData.country,
        }),
      );
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isError) {
      setIsLoading(false);
    }
  }, [isError]);

  useEffect(() => {
    if (
      authData.password &&
      authData.confirmPassword &&
      passwordRegex.test(authData.password) &&
      passwordRegex.test(authData.confirmPassword) &&
      authData.password !== authData.confirmPassword
    ) {
      setIsPasswordMatched("Password and confirm password must be same");
    } else {
      setIsPasswordMatched("");
    }
  }, [authData.password, authData.confirmPassword]);
  // New Code

  return (
    <>
      {steps?.userDataForm && (
        <>
          {/* Form Fields */}
          <InputField
            icon={PersonIcon}
            placeholder="Enter Full Name*"
            value={authData.fullName}
            onChange={onChangeHandler}
            name="fullName"
            type="text"
            error={!!errors.fullName}
            helperText={errors.fullName}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "4px",
              width: "100%",
            }}
          >
            <PhoneInput
              country={authData.country === "PH" ? "ph" : "us"}
              onlyCountries={["us", "ph"]}
              value={authData.phoneNumber}
              onChange={handlePhoneChange}
              placeholder={
              authData.country === "PH"
              ? "+63 912 345 6789*"
              : "+1 (702) 123-4567*"
                                    }
              inputStyle={{
                width: "100%",
                height: "50px",
                fontSize: "14px",
                backgroundColor: "#E5E4E2",
                border: errors.phoneNumber
                  ? "1px solid #D32F2F"
                  : "1px solid transparent",
                borderRadius: "48px",
                fontFamily: "SF Pro",
                color: "#000",
              }}
              buttonStyle={{
                backgroundColor: "#E5E4E2",
                border: "none",
                borderRadius: "48px 0 0 48px",
              }}
              containerStyle={{ width: "100%" }}
            />
            {errors.phoneNumber && (
              <Typography sx={{ fontSize: "12px", color: "#D32F2F", pl: 1 }}>
                {errors.phoneNumber}
              </Typography>
            )}
          </Box>

          {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "#E5E4E2",
                borderRadius: "48px",
                px: 1,
                minHeight: "50px",
                border: errors.state
                  ? "1px solid #D32F2F"
                  : "1px solid transparent",
              }}
            >
              <LocationOnIcon sx={{ mr: 0, color: "#666" }} />

              <Select
                value={authData.state || ""}
                onChange={onChangeHandler}
                name="state"
                displayEmpty
                fullWidth
                disableUnderline
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <span style={{ color: "#888888" }}>Select a State*</span>
                    );
                  }
                  return selected;
                }}
                sx={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: authData.state ? "#000" : "#888",

                  // remove outline completely
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                  "& .MuiSelect-select": {
                    paddingLeft: "8px", // or 4px / 8px
                  },
                }}
              >
                <MenuItem sx={{ pl: -1 }} value="" disabled>
                  <em>Select a State*</em>
                </MenuItem>

                {StateOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {errors.state && (
              <FormHelperText sx={{ mt: "-4px", pl: 0, color: "#D32F2F" }}>
                {errors.state || "Please select a state"}
              </FormHelperText>
            )}
          </Box> */}

          <Dropdown
            placeholder={
              authData.country === "PH"
                ? "Please select province*"
                : "Please select state*"
            }
            name="state"
            Icon={<LocationOnIcon sx={{ mr: 0, color: "#666" }} />}
            value={authData.state || ""}
            onChange={onChangeHandler}
            error={errors?.state}
            Options={
              authData.country === "PH"
                ? PhilippinesProvinceOptions
                : StateOptions
            }
          />

          <InputField
            icon={AddressIcon}
            placeholder="Enter City*"
            value={authData.city}
            onChange={onChangeHandler}
            name="city"
            type="text"
            error={!!errors.city}
            helperText={errors.city}
          />
          {/* <InputField
            icon={AddressIcon}
            placeholder="Enter State*"
            value={authData.state}
            onChange={onChangeHandler}
            name="state"
            type="text"
            error={!!errors.state}
            helperText={errors.state}
          /> */}
          <InputField
            icon={EmailIcon}
            placeholder="Enter Email Address*"
            value={authData.email}
            onChange={onChangeHandler}
            name="email"
            type="email"
            error={!!errors.email}
            helperText={errors.email}
          />
          <PasswordInputFiled
            icon={LockIcon}
            placeholder="Enter password*"
            value={authData.password}
            onChange={onChangeHandler}
            name="password"
            error={!!errors.password}
            helperText={errors.password}
          />
          <PasswordInputFiled
            icon={LockIcon}
            placeholder="Re-type password*"
            value={authData.confirmPassword}
            onChange={onChangeHandler}
            name="confirmPassword"
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
          />
          {isPasswordMatched && (
            <Typography color="#B3261E" fontSize={12} mt={-1} mb={1}>
              {isPasswordMatched}
            </Typography>
          )}
          <Box
            sx={{
              display: "flex",
              // justifyContent: "flex-start",
              flexDirection: "column",
              // alignItems: "center",
              width: "100%",
              // mt: 3,
              px: 1,
              gap: 2,
            }}
          >
            <FormControlLabel
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
              control={
                <Checkbox
                  sx={{
                    color: "#CAC4D0",
                    "&.Mui-checked": { color: "#0A0A0A" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "14px", fontFamily: "SF Pro" }}>
                  By checking this box, I consent to receive marketing and
                  promotional messages, including special offers, discounts and
                  new services updates from MyServiceApp LLC (DBA MyServiceApp).
                  Message frequency may vary. Message & Data rates may apply.
                  Reply HELP for help or STOP to opt out.
                </Typography>
              }
            />

            <FormControlLabel
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
              control={
                <Checkbox
                  sx={{
                    color: "#CAC4D0",
                    "&.Mui-checked": { color: "#0A0A0A" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "14px", fontFamily: "SF Pro" }}>
                  By checking this box, I consent to receive non-marketing text
                  messages from MyServiceApp LLC (DBA MyServiceApp) about
                  referral program updates, support request updates, account
                  updates and onboarding instructions, project status updates,
                  and service coordination. Message frequency may vary. Message
                  & data rates may apply. Text HELP for assistance, reply STOP
                  to opt out.
                </Typography>
              }
            />
          </Box>
          {/* Sign Up Button */}
          <Button
            onClick={step1Handler}
            // disabled={isLoading}
            sx={{
              width: "100%",
              borderRadius: "100px",
              backgroundColor: "#0A0A0A",
              color: "white !important",
              textTransform: "none",
              fontFamily: "SF Pro",
              fontWeight: 500,
              fontSize: 14,
              lineHeight: "16px",
              px: 4,
              paddingY: "16px",
              "&:hover": {
                backgroundColor: "#0A0A0A",
              },
            }}
          >
            Next
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              ml: 1,
              alignItems: "center",
            }}
          >
            <NextLink href="/terms-and-conditions" passHref legacyBehavior>
              <Link underline="hover" fontSize={11} color="#0000FF">
                Term and Conditions
              </Link>
            </NextLink>

            <NextLink href="/privacy-and-policy" passHref legacyBehavior>
              <Link underline="hover" fontSize={11} color="#0000FF">
                Privacy Policy
              </Link>
            </NextLink>
          </Box>
          {/* <Typography fontSize={12} color="#1F1B13" mt={2}>
            By proceeding, you consent to get calls, WhatsApp or SMS/RCS
            messages, including by automated dialer, from MSA and its affiliates
            to the number provided.
          </Typography> */}
        </>
      )}

      {steps?.termsAndServicesForm && (
        <TermsAndServices2
          authData={authData}
          setAuthData={setAuthData}
          stepsHandler={stepsHandler}
          isLoading={isLoading}
          registerUserHandler={registerUserHandler}
        />
      )}

      {/* {steps?.termsAndServicesForm && (
        <TermsAndServices
          authData={authData}
          setAuthData={setAuthData}
          stepsHandler={stepsHandler}
        />
      )} */}

      {/* {steps?.privacyPolicyForm && (
        <PrivacyPolicy
          authData={authData}
          setAuthData={setAuthData}
          stepsHandler={stepsHandler}
          isLoading={isLoading}
          registerUserHandler={registerUserHandler}
        />
      )} */}
    </>
  );
};

export default UserDataForm;
